import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { TimesIcon } from 'primeng/icons/times';

@Component({
  selector: 'nx-anchor-datetime-input',
  standalone: true,
  imports: [CalendarModule, FormsModule, OverlayPanelModule, TimesIcon],
  templateUrl: './anchor-datetime-input.component.html',
  styleUrls: ['./anchor-datetime-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AnchorDateTimeInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AnchorDateTimeInputComponent),
      multi: true,
    },
  ],
})
export class AnchorDateTimeInputComponent implements ControlValueAccessor, Validator {

  // ── Inputs ──────────────────────────────────────────────────────────────────

  readonly anchorDate  = input.required<Date>();
  readonly disabled    = input<boolean>(false);
  readonly placeholder = input<string>('HH:MM');
  readonly ariaLabel   = input<string>('Zeit');
  readonly minDate     = input<Date | null>(null);
  readonly maxDate     = input<Date | null>(null);

  // ── Outputs ─────────────────────────────────────────────────────────────────

  readonly valueChange  = output<Date | null>();
  readonly dateSelected = output<Date>();
  readonly timeSelected = output<number>();

  // ── Internal state (signals) ─────────────────────────────────────────────────

  readonly selectedDate    = signal<Date | null>(null);
  readonly selectedMinutes = signal<number | null>(null);

  private readonly _formDisabled = signal(false);

  // ── Computed ─────────────────────────────────────────────────────────────────

  readonly hasValue   = computed(() => this.selectedDate() !== null || this.selectedMinutes() !== null);
  readonly isDisabled = computed(() => this.disabled() || this._formDisabled());

  readonly calendarMinDate = computed(() => {
    const d = this.minDate();
    if (!d) return undefined;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  });

  readonly calendarMaxDate = computed(() => {
    const d = this.maxDate();
    if (!d) return undefined;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  });

  // ── Static data ──────────────────────────────────────────────────────────────

  protected readonly timeSlots: { minutes: number; label: string }[] = Array.from(
    { length: 96 },
    (_, i) => ({
      minutes: i * 15,
      label: `${String(Math.floor((i * 15) / 60)).padStart(2, '0')}:${String((i * 15) % 60).padStart(2, '0')}`,
    })
  );

  // ── ViewChildren ─────────────────────────────────────────────────────────────

  private readonly timeScrollContainer = viewChild<ElementRef<HTMLElement>>('timeScrollContainer');
  private readonly timeInputEl         = viewChild<ElementRef<HTMLInputElement>>('timeInput');
  readonly op                          = viewChild.required<OverlayPanel>('op');

  // ── DI ───────────────────────────────────────────────────────────────────────

  private readonly el       = inject(ElementRef);
  private readonly datePipe = inject(DatePipe);

  // ── CVA callbacks ────────────────────────────────────────────────────────────

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  constructor() {
    // Close panel when component becomes disabled
    effect(() => {
      if (this.isDisabled()) this.op()?.hide();
    });

    // Re-trigger form validation when min/max constraints change
    effect(() => {
      this.minDate();
      this.maxDate();
      this.onValidatorChange();
    });
  }

  // ── ControlValueAccessor ─────────────────────────────────────────────────────

  writeValue(value: Date | null): void {
    if (value instanceof Date && !isNaN(value.getTime())) {
      this.selectedDate.set(new Date(value.getFullYear(), value.getMonth(), value.getDate()));
      this.selectedMinutes.set(value.getHours() * 60 + value.getMinutes());
    } else {
      this.selectedDate.set(null);
      this.selectedMinutes.set(null);
    }
  }

  registerOnChange(fn: (value: Date | null) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  registerOnValidatorChange(fn: () => void): void { this.onValidatorChange = fn; }

  setDisabledState(isDisabled: boolean): void {
    this._formDisabled.set(isDisabled);
  }

  // ── Validator ────────────────────────────────────────────────────────────────

  validate(_control: AbstractControl): ValidationErrors | null {
    // TC-30: Datum gewählt aber keine Uhrzeit → invalid
    if (this.selectedDate() !== null && this.selectedMinutes() === null) {
      return { dateWithoutTime: true };
    }
    // TC-58/59: Wert außerhalb min/max Range → invalid
    const value = this.buildCombinedValue();
    if (value !== null) {
      const min = this.minDate();
      const max = this.maxDate();
      if (min && max && min.getTime() > max.getTime()) return null; // ungültige Range → kein Constraint
      if (min && value.getTime() < min.getTime()) return { outOfRange: { actual: value, min } };
      if (max && value.getTime() > max.getTime()) return { outOfRange: { actual: value, max } };
    }
    return null;
  }

  // ── Display helpers ───────────────────────────────────────────────────────────

  getDisplayDate(): string {
    const d = this.selectedDate() ?? this.anchorDate();
    return this.datePipe.transform(d, 'd. MMM', undefined, 'de') ?? '';
  }

  getTimeDisplayValue(): string {
    const minutes = this.selectedMinutes();
    if (minutes === null) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  // ── Panel ─────────────────────────────────────────────────────────────────────

  onDatePrefixClick(event: Event): void {
    if (this.isDisabled()) return;
    this.op().toggle(event, this.el.nativeElement);
  }

  protected onInputFocus(event: FocusEvent): void {
    if (this.isDisabled()) return;
    this.op().show(event, this.el.nativeElement);
  }

  protected onInputClick(): void {
    if (this.isDisabled()) return;
    this.timeInputEl()?.nativeElement.select();
  }

  protected onPanelShow(): void {
    setTimeout(() => this.scrollToSelectedTime(), 0);
  }

  protected onPanelHide(): void {
    this.onTouched();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.op()?.overlayVisible) return;
    this.op().hide();
    this.timeInputEl()?.nativeElement.focus();
    this.onTouched();
  }

  // ── Date selection ────────────────────────────────────────────────────────────

  onDateSelect(date: Date): void {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    this.selectedDate.set(d);
    this.dateSelected.emit(d);
    this.buildAndEmit();
    // Panel stays open — user should select time next (TC-08)
  }

  // ── Time selection ────────────────────────────────────────────────────────────

  onTimeSlotClick(minutes: number): void {
    if (this.isTimeSlotDisabled(minutes)) return; // TC-47
    // TC-12: implicitly use anchorDate when no date selected yet
    if (this.selectedDate() === null) {
      const a = this.anchorDate();
      this.selectedDate.set(new Date(a.getFullYear(), a.getMonth(), a.getDate()));
    }
    this.selectedMinutes.set(minutes);
    this.timeSelected.emit(minutes);
    this.buildAndEmit();
    this.op().hide();
    this.onValidatorChange();
  }

  // ── Free-text input ───────────────────────────────────────────────────────────

  protected onInputBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value;
    if (raw) {
      const parsed = parseRawTimeInput(raw);
      if (parsed === null) {
        input.value = this.getTimeDisplayValue(); // reset to previous
      } else {
        // TC-52/56: range-check after snap — before committing the value
        const refDate = this.getRefDate();
        const slotTime = new Date(
          refDate.getFullYear(), refDate.getMonth(), refDate.getDate(),
          Math.floor(parsed / 60), parsed % 60
        );
        const min = this.minDate();
        const max = this.maxDate();
        const invalidRange = !!(min && max && min.getTime() > max.getTime());
        const outOfRange = !invalidRange && (
          (!!min && slotTime.getTime() < min.getTime()) ||
          (!!max && slotTime.getTime() > max.getTime())
        );

        if (outOfRange) {
          input.value = this.getTimeDisplayValue(); // reset
        } else {
          if (this.selectedDate() === null) {
            const a = this.anchorDate();
            this.selectedDate.set(new Date(a.getFullYear(), a.getMonth(), a.getDate()));
          }
          this.selectedMinutes.set(parsed);
          this.buildAndEmit();
          this.onValidatorChange();
        }
      }
    }

    // Close overlay when focus actively moves outside (e.g. Tab).
    // Clicks on non-focusable overlay items (calendar days, time slots) produce
    // relatedTarget = null — in that case we leave the overlay open.
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (relatedTarget) {
      const inOverlay = relatedTarget.closest('.anchor-datetime-input-overlay') !== null;
      if (!inOverlay && !this.el.nativeElement.contains(relatedTarget)) {
        this.op().hide();
      }
    }
  }

  // ── Clear ─────────────────────────────────────────────────────────────────────

  onClearClick(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedDate.set(null);
    this.selectedMinutes.set(null);
    this.buildAndEmit();
    this.op().hide();
    this.onTouched();
    this.onValidatorChange();
  }

  // ── Build & emit ──────────────────────────────────────────────────────────────

  private buildAndEmit(): void {
    const value = this.buildCombinedValue();
    this.onChange(value);
    this.valueChange.emit(value);
  }

  buildCombinedValue(): Date | null {
    const date = this.selectedDate();
    const minutes = this.selectedMinutes();
    if (date === null || minutes === null) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), Math.floor(minutes / 60), minutes % 60, 0, 0);
  }

  // ── Scroll ────────────────────────────────────────────────────────────────────

  private scrollToSelectedTime(): void {
    const container = this.timeScrollContainer()?.nativeElement;
    if (!container) return;

    let targetIndex: number;
    if (this.selectedMinutes() !== null) {
      targetIndex = this.selectedMinutes()! / 15;
    } else {
      const defaultIndex = 24; // 06:00
      const min = this.minDate();
      if (min) {
        const refDate = this.getRefDate();
        const minDay = new Date(min.getFullYear(), min.getMonth(), min.getDate());
        if (refDate.getTime() === minDay.getTime()) {
          const minIndex = Math.floor((min.getHours() * 60 + min.getMinutes()) / 15);
          targetIndex = Math.max(defaultIndex, minIndex);
        } else {
          targetIndex = defaultIndex;
        }
      } else {
        targetIndex = defaultIndex;
      }
    }

    const items = container.querySelectorAll('.anchor-datetime-input__time-slot');
    (items[targetIndex] as HTMLElement)?.scrollIntoView({ block: 'center' });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  private getRefDate(): Date {
    return this.selectedDate() ?? (() => {
      const a = this.anchorDate();
      return new Date(a.getFullYear(), a.getMonth(), a.getDate());
    })();
  }

  isTimeSlotDisabled(minutes: number): boolean {
    const min = this.minDate();
    const max = this.maxDate();
    if (!min && !max) return false;
    if (min && max && min.getTime() > max.getTime()) return false; // ungültige Range → kein Constraint

    const ref = this.getRefDate();
    const slotTime = new Date(
      ref.getFullYear(), ref.getMonth(), ref.getDate(),
      Math.floor(minutes / 60), minutes % 60
    );
    if (min && slotTime.getTime() < min.getTime()) return true;
    if (max && slotTime.getTime() > max.getTime()) return true;
    return false;
  }
}

/**
 * Parses raw free-text time input into minutes since midnight, snapped to 15-minute grid.
 * Exported for testability.
 */
export function parseRawTimeInput(raw: string): number | null {
  const digits = raw.replace(/\D/g, '');
  let h: number;
  let m: number;

  if (digits.length === 3) {
    h = parseInt(digits[0], 10);
    m = parseInt(digits.slice(1), 10);
  } else if (digits.length === 4) {
    h = parseInt(digits.slice(0, 2), 10);
    m = parseInt(digits.slice(2, 4), 10);
  } else {
    return null;
  }

  if (h < 0 || h > 23 || m < 0 || m > 59) return null;

  const totalMinutes = h * 60 + m;
  return Math.min(Math.round(totalMinutes / 15) * 15, 1425);
}
